import React from 'react'
import { MaybeLoaded } from 'json-react-layouts-data-loader'
import { withTheme } from '@emotion/react'

import { ImageWidths } from 'common/helpers/image'
import { CommonCardProps } from 'component-library/cards/CardItem.Props'
import { ThemeProps } from 'component-library/__styling/themes'

import {
    StyledInlineContent,
    StyledCardMedia,
    StyledLink,
    StyledContentWrapper,
    StyledCardText,
    StyledCardByline,
    StyledSponsoredLabel,
} from 'component-library/cards/InlineContent/InlineContent.styled'
import { FontScales } from 'component-library/__styling/settings/fontScale'
import { ThemeMargins } from 'component-library/__styling/settings/metrics'
import { MeasureContents } from 'component-library/__helpers/measure-contents'
import { runAfterNextRepaint } from 'common/helpers/run-after-repaint'
import { toLinkWithHint } from 'common/routing/route-hints'
import { CardVideoDuration } from 'component-library/cards/CardVideoDuration/CardVideoDuration'
import { KickerPosition } from '../Kicker/Kicker.styled'
import { FixedRatio } from 'common/client-data-types/image-item'
import { MediaMode } from '../CardMedia/CardMedia'
import {
    PublicationCardItem,
    isPublicationCardItemWithVideo,
} from 'common/client-data-types/card-types'
import { createCardClickedEvent } from 'common/events/collection-events'

export interface Dimensions {
    width: number
    height: number
}

export interface State {
    layoutMode: 'auto' | 'landscape' | 'portrait'
}

export interface Props extends CommonCardProps {
    hasPadding?: boolean
    hasVerticalSpacing?: boolean
    mediaMode?: MediaMode
    hideText?: boolean
    // defaults to `16:9`
    fixedRatio?: FixedRatio
    imageWidths?: ImageWidths['thewest'] | ImageWidths['perthnow']
    fontScale: FontScales
    textVerticalSpacing?: keyof ThemeMargins
    isSponsoredCollection?: boolean
    showVideoDuration?: boolean
    kickerPosition?: KickerPosition
    layoutMode?: 'auto' | 'landscape' | 'portrait'
    overrideLayoutMode?: boolean
    largeKicker?: boolean
    onClick?: (e: React.MouseEvent<HTMLElement>) => void

    cardNumber: number
    item: MaybeLoaded<PublicationCardItem>
}

/**
 * The inline content card defines a card type which can be rendered from within an article. The card has two
 * layout formats, "landscape" and "portrait". The card beigns in landscape mode and swaps to portrait if the
 * text content is larger than the media. This is in order to avoid empty space under the media in "landscape"
 * mode (caused by the text size). The component reverses back if the browser is resized larger than the point
 * it swapped
 */
class InternalInlineContent extends React.Component<Props & ThemeProps, State> {
    static getDerivedStateFromProps(props: Props & ThemeProps, state: State) {
        if (props.layoutMode && props.layoutMode !== state.layoutMode) {
            return {
                layoutMode: props.layoutMode,
            }
        }
        return null
    }

    private mediaDimensions: null | Dimensions = null
    private textDimensions: null | Dimensions = null
    private swappedLayoutAtWidth: null | number = null

    constructor(props: Props & ThemeProps) {
        super(props)
        this.state = {
            layoutMode: this.props.layoutMode || 'auto',
        }
    }

    linkClicked = (e: React.MouseEvent<HTMLElement>) => {
        if (!this.props.item.loaded) {
            // We don't want the link to do anything
            e.preventDefault()
            return
        }

        if (this.props.onClick) {
            this.props.onClick(e)
        }

        this.props.onEvent(
            createCardClickedEvent(
                this.props.item.result,
                'InternalInlineContent',
                'InlineContent',
                this.props.cardNumber
            )
        )
    }

    handleNewDimensions = () => {
        if (
            !this.mediaDimensions ||
            !this.textDimensions ||
            !this.props.item.loaded ||
            this.props.overrideLayoutMode
        ) {
            return
        }

        // if the text is larger than the media, then force portriat mode

        const mediaHeight = this.mediaDimensions.height
        const textHeight = this.textDimensions.height
        const swapBias = 15

        if (this.state.layoutMode === 'auto' && textHeight > mediaHeight) {
            // swappedLayoutAtWidth is set so we can bring back landspace when we know the browser is big enough
            // We add a bias to the swap width because .
            this.swappedLayoutAtWidth =
                this.mediaDimensions.width + this.textDimensions.width + swapBias
            this.setState({
                layoutMode: 'portrait',
            })
        } else if (this.state.layoutMode === 'portrait' && this.swappedLayoutAtWidth) {
            if (this.mediaDimensions.width > this.swappedLayoutAtWidth) {
                this.swappedLayoutAtWidth = null
                this.setState({ layoutMode: 'auto' })

                // because of the debounce on resizes, swappedLayoutAtWidth isn't always the perfect number,
                // so we need to re-test after a repaint to make sure we got it right.
                runAfterNextRepaint(this.handleNewDimensions)
            }
        }
    }

    handleNewMediaDimensions = (width: number, height: number) => {
        this.mediaDimensions = { width, height }
        // we need to wait for flexbox to completelly repaint the card
        runAfterNextRepaint(this.handleNewDimensions)
    }

    handleNewTextDimensions = (width: number, height: number) => {
        this.textDimensions = { width, height }
        // we need to wait for flexbox to completelly repaint the card
        runAfterNextRepaint(this.handleNewDimensions)
    }

    render() {
        const {
            theme,
            showVideoDuration = false,
            hasPadding,
            hasVerticalSpacing,
            item,
        } = this.props

        const fixedRatio = this.props.fixedRatio || '16:9'

        // SWM-4731 When the page is sponsored, cards should never show the sponsored status
        const showSponsoredLabel =
            !item.loaded || this.props.isSponsoredCollection ? false : item.result.isSponsored

        const showByline = theme.kind !== 'perthnow' && !this.props.hideByline

        // determine if we are displaying an Opinion Card
        const isOpinionPublication =
            this.props.item.loaded && this.props.item.result.primaryTopic.id.indexOf('opinion') > -1
        const isWestOpinionCard = theme.kind === 'thewest' && isOpinionPublication
        const opinionMediaMode = isWestOpinionCard && this.props.mediaMode !== 'hidden'
        const mediaMode = opinionMediaMode ? 'edgeToEdge' : this.props.mediaMode

        const layoutMode =
            this.props.overrideLayoutMode && this.props.layoutMode
                ? this.props.layoutMode
                : this.state.layoutMode

        return (
            <StyledInlineContent
                hasVerticalSpacing={hasVerticalSpacing}
                isLoading={!this.props.item.loaded}
            >
                <StyledLink
                    to={toLinkWithHint(item)}
                    onClick={this.linkClicked}
                    hasBackground={true}
                    layoutMode={layoutMode}
                    hasPadding={hasPadding}
                >
                    {this.props.mediaMode !== 'hidden' && (
                        <MeasureContents onNewDimensions={this.handleNewMediaDimensions}>
                            {ref => (
                                <StyledCardMedia
                                    setInnerRef={ref}
                                    layoutMode={layoutMode}
                                    item={item}
                                    fixedRatio={fixedRatio}
                                    hasBackground={this.props.item.loaded}
                                    imageWidths={this.props.imageWidths}
                                    onEvent={this.props.onEvent}
                                    disableImageLazyLoad={this.props.disableImageLazyLoad}
                                    willPlayVideoInline={true}
                                    mediaMode={mediaMode}
                                />
                            )}
                        </MeasureContents>
                    )}
                    {!this.props.hideText && (
                        <MeasureContents onNewDimensions={this.handleNewTextDimensions}>
                            {ref => (
                                <StyledContentWrapper isLoading={!this.props.item.loaded} ref={ref}>
                                    <StyledCardText
                                        item={item}
                                        largeKicker={this.props.largeKicker}
                                        hasBackground={this.props.item.loaded}
                                        hideKicker={this.props.hideKicker}
                                        hideTeaser={this.props.hideTeaser}
                                        fontScale={this.props.fontScale}
                                        onEvent={this.props.onEvent}
                                        verticalSpacing={this.props.textVerticalSpacing}
                                        kickerPosition={this.props.kickerPosition}
                                    />

                                    {showByline && (
                                        <StyledCardByline
                                            showBottomPadding={!showVideoDuration}
                                            item={item}
                                            onEvent={this.props.onEvent}
                                            showProfile={false}
                                            isEdgeToEdgeCard={false}
                                            hasBackground={this.props.item.loaded}
                                        />
                                    )}

                                    {showVideoDuration &&
                                        item.loaded &&
                                        isPublicationCardItemWithVideo(item.result) && (
                                            <CardVideoDuration item={item.result} />
                                        )}
                                    {showSponsoredLabel && (
                                        <StyledSponsoredLabel hasBackground={true} />
                                    )}
                                </StyledContentWrapper>
                            )}
                        </MeasureContents>
                    )}
                </StyledLink>
            </StyledInlineContent>
        )
    }
}

export const InlineContent = withTheme(InternalInlineContent)
